import { useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { SchemaAzienda, aggiornaAzienda, elencaAziende, type Azienda, type DatiAzienda } from './aziende.api'

const NESSUNA: Azienda[] = []

const CAMPI = [
  ['ragioneSociale', 'Ragione sociale'],
  ['codiceFiscale', 'Codice fiscale'],
  ['partitaIva', 'Partita IVA'],
  ['telefono', 'Telefono'],
  ['email', 'Email'],
] as const

const daAzienda = (a: Azienda): DatiAzienda => ({
  ragioneSociale: a.ragioneSociale,
  codiceFiscale: a.codiceFiscale,
  partitaIva: a.partitaIva ?? '',
  telefono: a.telefono ?? '',
  email: a.email ?? '',
})

export function ElencoAziende() {
  const { idPartner } = useParams()
  const queryClient = useQueryClient()
  const [cerca, setCerca] = useState('')
  const [modifica, setModifica] = useState<{ id: number; nome: string; dati: DatiAzienda } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  const { data: aziende = NESSUNA, error } = useQuery({ queryKey: ['aziende'], queryFn: elencaAziende })

  // Ricerca per ragione sociale o codice fiscale: sono i due modi in cui si
  // cerca un cliente al telefono.
  const filtro = cerca.trim().toLowerCase()
  const visibili = aziende.filter(
    (a) => filtro === '' || `${a.ragioneSociale} ${a.codiceFiscale}`.toLowerCase().includes(filtro),
  )

  async function suSalva() {
    if (modifica === null) return
    const esito = SchemaAzienda.safeParse(modifica.dati)
    if (!esito.success) {
      setErrore(esito.error.issues[0]!.message)
      return
    }
    setErrore(null)
    try {
      await aggiornaAzienda(modifica.id, modifica.dati)
      setModifica(null)
      await queryClient.invalidateQueries({ queryKey: ['aziende'] })
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Aziende
      </Typography>
      {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error instanceof Error ? error.message : 'Errore nel caricamento'}</Alert>}

      <TextField
        size="small"
        label="Cerca azienda"
        value={cerca}
        onChange={(e) => setCerca(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ragione sociale</TableCell>
            <TableCell>Codice fiscale</TableCell>
            <TableCell>Contatti</TableCell>
            <TableCell>Impianti</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {visibili.map((a) => (
            <TableRow key={a.id}>
              <TableCell>{a.ragioneSociale}</TableCell>
              <TableCell>{a.codiceFiscale}</TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {[a.email, a.telefono].filter(Boolean).join(' · ') || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                {a.impianti === 0 ? (
                  'Nessun impianto'
                ) : (
                  <Link component={RouterLink} to={`/cliente/${idPartner}/impianti?azienda=${a.id}`}>
                    {a.impianti} {a.impianti === 1 ? 'impianto' : 'impianti'}
                  </Link>
                )}
              </TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  aria-label={`Modifica ${a.ragioneSociale}`}
                  onClick={() => {
                    setErrore(null)
                    setModifica({ id: a.id, nome: a.ragioneSociale, dati: daAzienda(a) })
                  }}
                >
                  Modifica
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {visibili.length === 0 && <p>Nessuna azienda</p>}

      <Dialog open={modifica !== null} onClose={() => setModifica(null)} fullWidth maxWidth="sm">
        <DialogTitle>{modifica?.nome}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
            {CAMPI.map(([campo, etichetta]) => (
              <TextField
                key={campo}
                size="small"
                label={etichetta}
                value={modifica?.dati[campo] ?? ''}
                onChange={(e) =>
                  setModifica((m) => (m === null ? m : { ...m, dati: { ...m.dati, [campo]: e.target.value } }))
                }
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModifica(null)}>Annulla</Button>
          <Button variant="contained" onClick={() => void suSalva()}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
